import type { ViewQueryList } from '@salescore/core'
import { Array, pipe } from 'effect'

import { getDefaultAttributes } from '../../recoil/records/selectors/querySelector'
import type { NewRecordChange } from '../../rsheet/action/addNewRecord/addNewRecords'
import { getRecordNodeWithInnerRowIndex } from '../../rsheet/util/innerRowRelated'
import type { RecordChange } from '../../rsheet/util/updateViewRecords'
import type { RSheetRecordNode } from '../column'
import { isPlaceholderCellValue } from '../copyMatrix'
import type { ChangeCandidate } from './generateChangeCandidates'
import { keyFunction } from './generateParentToChildRelationChanges'

export const generateNewRecordChanges = (
  changeCandidates: ChangeCandidate[],
  records: RSheetRecordNode[],
  relationChangeKeySet: Set<string>,
  query: ViewQueryList | undefined,
): { newRecordChanges: NewRecordChange[]; defaultValueChanges: RecordChange[] } => {
  const newRecordChanges = pipe(
    changeCandidates,
    Array.filter(({ value }) => !isPlaceholderCellValue(value)),
    Array.dedupeWith(
      (a, b) =>
        a.column.node.path.join(',') === b.column.node.path.join(',') &&
        a.rowIndex === b.rowIndex &&
        a.innerRowIndex === b.innerRowIndex,
    ),
    Array.filter(({ column }) => column.nodeType !== 'parent'), // 虫眼鏡ノードへの新規レコード作成は行わない
    Array.filter(
      ({ column, rowIndex, innerRowIndex }) =>
        (records[rowIndex] === undefined && // 新規ルートレコード追加の場合
          (column.node.path.length === 1 || // ルートノードの場合、問答無用でレコードを作る
            !relationChangeKeySet.has(
              // 子ノードの場合、 relationChange で自動生成されない場合のみレコードを作る
              keyFunction({
                nodePath: column.node.path,
                rowIndex,
                innerRowIndex,
              }),
            ))) ||
        (records[rowIndex] !== undefined && // ルートレコードがすでに存在している場合
          getRecordNodeWithInnerRowIndex(records[rowIndex], column.node, innerRowIndex)?.recordNode === undefined && // かつ、子ノードが存在しない場合
          !relationChangeKeySet.has(
            // かつ relationChange で自動生成されない場合のみレコードを作る
            keyFunction({
              nodePath: column.node.path,
              rowIndex,
              innerRowIndex,
            }),
          )),
    ),
    Array.map(({ column, rowIndex, innerRowIndex, height }) => ({
      node: column.node,
      rowIndex,
      innerRowIndex,
      parentSelectionInnerRowIndex: innerRowIndex,
      height,
    })),
  )

  const defaultValueChanges = pipe(
    newRecordChanges,
    Array.flatMap(({ node, rowIndex, innerRowIndex }) =>
      pipe(
        getDefaultAttributes(node, query).defaultValues,
        Array.map(({ field, value }) => ({
          rowIndex,
          innerRowIndex, // NOTE: upsertViewRecordsMutation側で新規生成されたレコードのinnerRowIndexに書き変わる(子ブロックの際は基本的に+1される、子レコードがない状態から新規作成した場合はindexは変わらない)
          field,
          nodePath: field.nodePath,
          isNestedColumn: false,
          value,
          label: undefined,
        })),
      ),
    ),
  )

  return { newRecordChanges, defaultValueChanges }
}
